import _ from 'lodash'
import classNames from 'classnames'
import React, {useContext, useEffect, useState} from 'react'
import {NavLink} from 'react-router-dom'
import {BsChevronDown} from 'react-icons/bs'
import useTranslate from 'modules/local/useTranslate'
import Translate from 'modules/local/Translate'
import {hasPermissions} from 'views/AppLocals/factory/createCheckPermissions'
import {useRouteSearchParam} from '../../../envs/_current/history'
import {isDevEnviroment} from '../../../envs/ForDevelop'
import WorkspaceContext from '../WorkspaceContext'

// Tách từ Workspace.js để DÙNG CHUNG với AccessPortalPage.js (portal Khóa học qua
// feature_key=organization_manage_course) - trước đây chỉ Workspace.js dùng, giờ 2 nơi cần y hệt 1
// component (sửa 1 chỗ, áp dụng cả 2 - đúng yêu cầu).
//
// buildUrl(itemKey): THAY cho baseUrl string + tự nối "?rel=" thô như bản gốc - Workspace.js nối
// vào 1 path THUẦN (`/workspace/:idname/course`, chưa có query) nên nối trực tiếp luôn đúng. Access-
// portal thì URL gốc ĐÃ có sẵn `?org_id=&feature_key=` - nối thô kiểu cũ sẽ ra `...?org_id=X?rel=Y`
// (2 dấu ? - sai). Buộc caller tự cung cấp hàm build URL đúng ngữ cảnh của mình thay vì đoán.
//
// checkPermissions=false: bỏ qua hasPermissions() hoàn toàn, coi mọi item luôn enabled - dùng khi
// KHÔNG có PermissionContext thật (access-portal chỉ set WorkspaceContext.Provider value={{organization}},
// không có permissions - checkAccess() phía BE đã tự xác nhận quyền trước khi vào được trang).

export const ComingSoonBadge = () => (
    <Translate>
        {t => (
            <span
                style={{padding: '4px 8px'}}
                className="bg-yellow-50 font-medium text-2xs text-yellow-400 italic leading-none">
                {t('coming soon')}
            </span>
        )}
    </Translate>
)

export const hasActiveDescendant = (item, rel) => {
    if (!item.children) return false
    return item.children.some(child => child.key === rel || hasActiveDescendant(child, rel))
}

// KHÔNG xoá items có isUnderDevelopment ngoài DEV, giữ nguyên đúng hành vi gốc ở Workspace.js -
// hàm này chỉ tách vị trí, logic y hệt (in non-DEV: removes items with isUnderDevelopment and
// collapses empty groups).
export const filterDevItems = (items = []) => {
    if (isDevEnviroment()) return items
    return items
        .filter(item => !item.isUnderDevelopment)
        .map(item => {
            if (!item.children) return item
            const children = filterDevItems(item.children)
            return children.length > 0 ? {...item, children} : null
        })
        .filter(Boolean)
}

export const WorkspaceSidebarSubItem = ({item, buildUrl, permissions, checkPermissions = true}) => {
    const t = useTranslate()

    const {organization} = useContext(WorkspaceContext)
    const isOwner = organization?.is_owner

    const isDisabled = checkPermissions && (
        (!_.isEmpty(item.required_permissions) && !hasPermissions(permissions, item.required_permissions)) ||
        (item.owner_only && !isOwner)
    )

    const hasChildren = !_.isEmpty(item.children)
    const rel = useRouteSearchParam('rel') || 'home'
    const itemKey = item?.key || 'home'
    const isActive = rel === itemKey
    const isChildActive = hasChildren && hasActiveDescendant(item, rel)
    const [open, setOpen] = useState(isChildActive)

    useEffect(() => {
        if (isChildActive) setOpen(true)
    }, [isChildActive])
    const leafUrl = hasChildren ? null : buildUrl(itemKey)

    const linkClass = classNames(
        'flex items-center gap-2 no-underline hover:pl-1 transition-all duration-300',
        isActive ? 'font-semibold text-secondary-600' : 'text-slate-500 hover:text-secondary',
        isDisabled && 'opacity-40 pointer-events-none',
    )

    const toggleOpen = (e) => {
        if (isDisabled) return
        e.preventDefault()
        e.stopPropagation()
        setOpen(v => !v)
    }

    return (
        <div className="flex flex-col flex-1 gap-1 SidebarSubsItem">
            {hasChildren ? (
                <div
                    onClick={toggleOpen}
                    className={classNames(
                        'p-1 flex items-center gap-2 cursor-pointer rounded-md',
                        isActive
                            ? 'font-bold text-secondary-700'
                            : open
                            ? 'font-semibold text-slate-600'
                            : 'text-slate-500 hover:text-secondary-600 hover:bg-secondary-50',
                        isDisabled && 'opacity-40 pointer-events-none',
                    )}>
                    <span className="flex-1 tracking-wide truncate">
                        {t(item.label)}
                    </span>
                    <BsChevronDown
                        size={14}
                        style={{transform: open ? 'rotate(-90deg)' : ''}}
                        className="SidebarSubItemIcon mr-1 transition-all duration-300"
                    />
                </div>
            ) : (
                <div className="p-1 flex items-center gap-2 transition-all duration-300 SidebarSubsItem">
                    <NavLink to={leafUrl} className={classNames(linkClass, 'flex-1')}>
                        {/* item.icon: ngoại lệ có chủ đích, hiện chỉ dùng cho nhánh "lookup the
                            sacraments" để phân biệt trực quan từng loại bí tích - các submenu khác
                            trong Workspace không truyền icon nên vẫn giữ nguyên text-only. */}
                        {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                        <span className="flex-1 truncate">{t(item.label)}</span>
                    </NavLink>
                    {item.badge}
                    {item.isUnderDevelopment && <ComingSoonBadge/>}
                </div>
            )}

            {hasChildren && open && (
                <div className="ml-3 pl-3 border-l border-color-100 flex flex-col gap-1">
                    {item.children.map(child => (
                        <WorkspaceSidebarSubItem
                            key={child.key}
                            item={child}
                            buildUrl={buildUrl}
                            permissions={permissions}
                            checkPermissions={checkPermissions}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}
